import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Input } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useMemo, useState } from "react";
import { lintPcre } from "utils/pcreLint";
import { hasUnescapedSlash } from "utils/regexValidation";

interface RegexTesterProps {
    /** The pattern being edited, the tester stays hidden until it is a usable one */
    pattern: string | null | undefined;
    /** BE wraps the pattern in slashes, so a bare slash makes it unusable there */
    isBackend?: boolean;
}

type Preview = { kind: "regex"; regex: RegExp } | { kind: "unavailable" } | null;

const compile = (pattern: string | null | undefined, isBackend: boolean): Preview => {
    if (!pattern) {
        return null;
    }
    if (isBackend) {
        if (hasUnescapedSlash(pattern)) {
            return null;
        }
        const { errors, pcreOnly } = lintPcre(pattern);
        if (errors.length > 0) {
            return null;
        }
        // PCRE syntax the browser engine lacks or reads differently: a result here would not say what BE does
        if (pcreOnly) {
            return { kind: "unavailable" };
        }
    }
    try {
        return { kind: "regex", regex: new RegExp(pattern) };
    } catch {
        return null;
    }
};

/** Live check of a sample value against the regex being edited, nothing here is saved. */
export const RegexTester = ({ pattern, isBackend = false }: RegexTesterProps) => {
    const { t } = usePaldockTranslation("forms.form-field-settings");
    const [sample, setSample] = useState("");
    const preview = useMemo(() => compile(pattern, isBackend), [pattern, isBackend]);

    if (!preview) {
        return null;
    }

    if (preview.kind === "unavailable") {
        return <div className="mb-4 text-xs text-gray-500">{t("regex_tester.pcre_only")}</div>;
    }

    const matches = preview.regex.test(sample);

    return (
        <div className="mb-4">
            <Input
                value={sample}
                onChange={(event) => setSample(event.target.value)}
                placeholder={t("regex_tester.placeholder")}
                status={sample && !matches ? "error" : undefined}
                // Always set: antd wraps the input when a suffix appears, which remounts it and drops the focus
                suffix={
                    <span className="inline-flex">
                        {sample &&
                            (matches ? (
                                <CheckCircleFilled className="text-green-600" title={t("regex_tester.match")} />
                            ) : (
                                <CloseCircleFilled className="text-red-500" title={t("regex_tester.no_match")} />
                            ))}
                    </span>
                }
            />
            {sample && (
                <span className={`text-xs ${matches ? "text-green-600" : "text-red-500"}`}>
                    {matches ? t("regex_tester.match") : t("regex_tester.no_match")}
                </span>
            )}
        </div>
    );
};
